import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  DollarSign, Plus, Edit, Trash2, X, Loader2, Search, Users, Package, Layers,
  ChevronRight, Info, Save,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const inputCls = "w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-[#C6A15B]/60 focus:outline-none";
const labelCls = "block text-xs font-medium text-slate-400 mb-1.5";
const money = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const Skeleton = ({ className = '' }) => <div className={`animate-pulse bg-white/[0.05] rounded-lg ${className}`} />;

const Modal = ({ open, onClose, title, icon: Icon, children, maxW = 'max-w-lg', testId }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className={`bg-[#101317] border border-white/10 rounded-2xl w-full ${maxW} max-h-[88vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()} data-testid={testId}>
        <div className="flex items-start justify-between p-5 border-b border-white/[0.06]">
          <div className="flex items-center gap-3">
            {Icon && <div className="w-9 h-9 rounded-xl bg-[#C6A15B]/15 flex items-center justify-center"><Icon className="w-5 h-5 text-[#C6A15B]" /></div>}
            <h3 className="text-base font-semibold text-slate-100">{title}</h3>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
};

/* ================================ PRICING TIERS ================================ */
const PricingTiers = () => {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ role: '', display_name: '', discount_percentage: '' });
  const [editRole, setEditRole] = useState(null);
  const [editVal, setEditVal] = useState({ display_name: '', discount_percentage: '' });

  const fetchTiers = async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/api/pricing`); setTiers(res.data || []); }
    catch { toast.error('Failed to load pricing tiers'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchTiers(); }, []);

  const create = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await axios.post(`${API}/api/pricing`, { ...form, discount_percentage: parseFloat(form.discount_percentage) });
      toast.success('Pricing tier created'); setShowCreate(false); setForm({ role: '', display_name: '', discount_percentage: '' }); fetchTiers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
    finally { setSaving(false); }
  };
  const saveEdit = async (role) => {
    try {
      await axios.put(`${API}/api/pricing/${role}`, { display_name: editVal.display_name, discount_percentage: parseFloat(editVal.discount_percentage) });
      toast.success('Tier updated'); setEditRole(null); fetchTiers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };
  const del = async (role, name) => {
    if (!window.confirm(`Delete tier "${name}"? Users with this role will see undiscounted prices.`)) return;
    try { await axios.delete(`${API}/api/pricing/${role}`); toast.success('Tier deleted'); fetchTiers(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-400">Automatic discounts applied by account role. Precedence: order-line &gt; customer price &gt; account % &gt; <span className="text-[#C6A15B]">tier %</span> &gt; base.</p>
        <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-[#C6A15B] hover:bg-[#b5924f] text-black text-sm font-semibold" data-testid="new-tier-btn"><Plus className="w-4 h-4" /> New Tier</button>
      </div>
      {loading ? <Skeleton className="h-40 w-full" /> : (
        <div className="bg-[#14171B] border border-white/[0.08] rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500 border-b border-white/[0.06]">
              <th className="p-4">Account Type</th><th className="p-4">Role Code</th><th className="p-4">Discount</th><th className="p-4">Example ($100)</th><th className="p-4 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {tiers.map(t => (
                <tr key={t.role} className="border-b border-white/[0.04]" data-testid={`tier-row-${t.role}`}>
                  <td className="p-4">
                    {editRole === t.role
                      ? <input className={inputCls} value={editVal.display_name} onChange={e => setEditVal({ ...editVal, display_name: e.target.value })} />
                      : <span className="text-slate-100 font-medium">{t.display_name}</span>}
                  </td>
                  <td className="p-4"><span className="text-[11px] px-2 py-0.5 rounded bg-white/5 text-slate-300 font-mono">{t.role}</span></td>
                  <td className="p-4">
                    {editRole === t.role
                      ? <input type="number" step="0.1" className={`${inputCls} w-24`} value={editVal.discount_percentage} onChange={e => setEditVal({ ...editVal, discount_percentage: e.target.value })} />
                      : <span className="text-xl font-bold text-[#C6A15B]">{t.discount_percentage}%</span>}
                  </td>
                  <td className="p-4"><span className="text-slate-500 line-through mr-2">$100.00</span><span className="text-emerald-400 font-semibold">{money(100 * (1 - t.discount_percentage / 100))}</span></td>
                  <td className="p-4">
                    <div className="flex justify-end gap-2">
                      {editRole === t.role ? (
                        <>
                          <button onClick={() => saveEdit(t.role)} className="text-xs px-3 py-1.5 rounded-lg bg-[#C6A15B] hover:bg-[#b5924f] text-black font-semibold">Save</button>
                          <button onClick={() => setEditRole(null)} className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-slate-300">Cancel</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => { setEditRole(t.role); setEditVal({ display_name: t.display_name, discount_percentage: String(t.discount_percentage) }); }} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200"><Edit className="w-3 h-3" /> Edit</button>
                          <button onClick={() => del(t.role, t.display_name)} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-red-500/40 text-red-400 hover:bg-red-500/10"><Trash2 className="w-3 h-3" /> Delete</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {tiers.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-slate-500">No pricing tiers configured.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create Pricing Tier" icon={Plus} testId="create-tier-dialog">
        <form onSubmit={create} className="space-y-4">
          <div><label className={labelCls}>Role Code *</label><input required className={inputCls} placeholder="e.g. premium_dealer" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} /></div>
          <div><label className={labelCls}>Display Name *</label><input required className={inputCls} placeholder="e.g. Premium Distributor" value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })} /></div>
          <div><label className={labelCls}>Discount % *</label><input required type="number" step="0.1" min="0" max="100" className={inputCls} placeholder="20.0" value={form.discount_percentage} onChange={e => setForm({ ...form, discount_percentage: e.target.value })} /></div>
          <div className="flex justify-end gap-2 pt-2 border-t border-white/[0.06]">
            <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-lg border border-white/10 text-sm text-slate-300">Cancel</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#C6A15B] hover:bg-[#b5924f] text-black text-sm font-semibold">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Tier'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

/* ===================== CUSTOMER PRICING (effective + overrides) ===================== */
const CustomerPricing = ({ overridesOnly = false }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [acctPct, setAcctPct] = useState('');
  const [overrides, setOverrides] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/users`);
      const list = (res.data.users || res.data || []).filter(u => ['installation_center', 'elite_partner'].includes(u.role));
      setCustomers(list);
    } catch { toast.error('Failed to load customers'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchCustomers(); }, []);

  const openCustomer = async (c) => {
    setSelected(c); setDetailLoading(true); setDetail(null);
    try {
      const [d, cp] = await Promise.all([
        axios.get(`${API}/api/customers/${c.user_id}/pricing-detail`),
        axios.get(`${API}/api/users/${c.user_id}/custom-pricing`),
      ]);
      setDetail(d.data);
      setAcctPct(cp.data.custom_discount_percent != null ? String(cp.data.custom_discount_percent) : '');
      setOverrides(cp.data.custom_prices || {});
    } catch (e) { toast.error('Failed to load pricing detail'); }
    finally { setDetailLoading(false); }
  };

  const saveOverrides = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const prices = {};
      Object.entries(overrides).forEach(([pid, v]) => { if (v !== '' && v != null && !isNaN(parseFloat(v))) prices[pid] = parseFloat(v); });
      await axios.put(`${API}/api/users/${selected.user_id}/custom-pricing`, {
        custom_discount_percent: acctPct === '' ? -1 : parseFloat(acctPct),
        custom_prices: prices,
      });
      toast.success('Pricing updated');
      openCustomer(selected); fetchCustomers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const filtered = useMemo(() => {
    let list = customers;
    if (overridesOnly) list = list.filter(c => (c.custom_prices && Object.keys(c.custom_prices).length) || c.custom_discount_percent != null);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(c => (c.full_name || '').toLowerCase().includes(q) || (c.company_name || '').toLowerCase().includes(q) || (c.username || '').toLowerCase().includes(q));
    }
    return list;
  }, [customers, query, overridesOnly]);

  return (
    <div className="grid lg:grid-cols-[1fr_1.4fr] gap-4">
      {/* list */}
      <div className="bg-[#14171B] border border-white/[0.08] rounded-2xl p-4">
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input className={`${inputCls} pl-9`} placeholder="Search customers…" value={query} onChange={e => setQuery(e.target.value)} data-testid="customer-pricing-search" />
        </div>
        {loading ? <Skeleton className="h-64 w-full" /> : (
          <div className="space-y-1.5 max-h-[520px] overflow-y-auto">
            {filtered.map(c => {
              const ovCount = c.custom_prices ? Object.keys(c.custom_prices).length : 0;
              const active = selected?.user_id === c.user_id;
              return (
                <button key={c.user_id} onClick={() => openCustomer(c)} className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors ${active ? 'border-[#C6A15B]/50 bg-[#C6A15B]/10' : 'border-transparent hover:bg-white/[0.03]'}`} data-testid={`customer-pricing-row-${c.user_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm text-slate-100 font-medium truncate">{c.company_name || c.full_name}</p>
                      <p className="text-xs text-slate-500 truncate">{c.role === 'elite_partner' ? 'Elite Partner' : 'Installation Center'}</p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {c.custom_discount_percent != null && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">{c.custom_discount_percent}%</span>}
                      {ovCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#C6A15B]/15 text-[#C6A15B]">{ovCount} override{ovCount > 1 ? 's' : ''}</span>}
                      <ChevronRight className="w-4 h-4 text-slate-600" />
                    </div>
                  </div>
                </button>
              );
            })}
            {filtered.length === 0 && <p className="text-sm text-slate-500 text-center py-8">{overridesOnly ? 'No customers have custom pricing yet.' : 'No customers found.'}</p>}
          </div>
        )}
      </div>

      {/* detail */}
      <div className="bg-[#14171B] border border-white/[0.08] rounded-2xl p-5">
        {!selected ? (
          <div className="flex flex-col items-center justify-center h-full py-16 text-center">
            <DollarSign className="w-12 h-12 text-slate-600 mb-3" />
            <p className="text-slate-300 font-medium">Select a customer</p>
            <p className="text-slate-500 text-sm mt-1">See their effective pricing and manage overrides.</p>
          </div>
        ) : detailLoading ? (
          <Skeleton className="h-96 w-full" />
        ) : detail ? (
          <div className="space-y-4" data-testid="customer-pricing-detail">
            <div>
              <h3 className="text-lg font-semibold text-slate-100">{selected.company_name || selected.full_name}</h3>
              <p className="text-sm text-slate-400">Tier: <span className="text-slate-200">{detail.tier_name}</span> ({detail.tier_discount}%) · Account discount: <span className="text-slate-200">{detail.account_discount_percent}%</span> <span className="text-slate-500">({detail.account_discount_source})</span></p>
            </div>

            <div className="bg-black/30 border border-white/10 rounded-xl p-4 space-y-3">
              <div>
                <label className={labelCls}>Account Discount % <span className="text-slate-600">(overrides tier; blank = use tier)</span></label>
                <input type="number" step="0.1" min="0" max="100" className={`${inputCls} max-w-[160px]`} placeholder="Use tier" value={acctPct} onChange={e => setAcctPct(e.target.value)} data-testid="account-discount-input" />
              </div>
            </div>

            <div className="border border-white/[0.06] rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-slate-500 border-b border-white/[0.06] bg-black/20">
                  <th className="p-3">Product</th><th className="p-3 text-right">Base</th><th className="p-3 text-right">Tier</th><th className="p-3 text-right">Override</th><th className="p-3 text-right">Effective</th>
                </tr></thead>
                <tbody>
                  {detail.rows.map(r => (
                    <tr key={r.product_id} className="border-b border-white/[0.03]">
                      <td className="p-3 text-slate-200">{r.name}</td>
                      <td className="p-3 text-right text-slate-400">{money(r.base_price)}</td>
                      <td className="p-3 text-right text-slate-400">{money(r.tier_price)}</td>
                      <td className="p-3 text-right">
                        <input type="number" step="0.01" min="0" className={`${inputCls} w-24 text-right py-1`} placeholder="—"
                          value={overrides[r.product_id] ?? ''} onChange={e => setOverrides({ ...overrides, [r.product_id]: e.target.value })} data-testid={`override-${r.product_id}`} />
                      </td>
                      <td className="p-3 text-right font-semibold text-[#C6A15B]">{money(r.effective_price)}<span className="block text-[10px] font-normal text-slate-500">{r.source}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-slate-500 flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> Override &gt; account % &gt; tier %. Effective shown after save.</p>
              <div className="flex gap-2">
                <button onClick={() => { setAcctPct(''); setOverrides({}); }} className="text-sm px-4 py-2 rounded-lg border border-white/10 text-slate-300">Clear All</button>
                <button onClick={saveOverrides} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#C6A15B] hover:bg-[#b5924f] text-black text-sm font-semibold" data-testid="save-customer-pricing-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Save</>}</button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

/* ================================ SHELL ================================ */
export const PricingWorkspace = () => {
  const [tab, setTab] = useState('tiers');
  const TABS = [
    { k: 'tiers', label: 'Pricing Tiers', icon: Layers },
    { k: 'customers', label: 'Customer Pricing', icon: Users },
    { k: 'overrides', label: 'Product Overrides', icon: Package },
  ];
  return (
    <div className="space-y-6" data-testid="pricing-workspace">
      <div>
        <h2 className="text-2xl font-bold text-slate-100 flex items-center gap-2.5"><DollarSign className="w-6 h-6 text-[#C6A15B]" /> Pricing</h2>
        <p className="text-sm text-slate-400 mt-1">Manage account tiers and customer-specific pricing — the engine's precedence is unchanged.</p>
      </div>
      <div className="flex gap-1 border-b border-white/[0.06]">
        {TABS.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.k ? 'border-[#C6A15B] text-[#C6A15B]' : 'border-transparent text-slate-400 hover:text-slate-200'}`} data-testid={`pricing-tab-${t.k}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>
      {tab === 'tiers' && <PricingTiers />}
      {tab === 'customers' && <CustomerPricing />}
      {tab === 'overrides' && <CustomerPricing overridesOnly />}
    </div>
  );
};

export default PricingWorkspace;
